import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Star, Crown, Heart, Sparkles } from 'lucide-react';

interface Feature {
  name: string;
  description: string;
  basic: boolean | string;
  premium: boolean | string;
  highlight?: boolean;
}

const features: Feature[] = [
  {
    name: "Cycle Tracking",
    description: "Track your periods and cycle patterns",
    basic: true,
    premium: true
  },
  {
    name: "Period Predictions",
    description: "AI-powered predictions for your next period",
    basic: "Basic",
    premium: "Advanced AI",
    highlight: true
  },
  {
    name: "Symptom Tracking",
    description: "Log and analyze your symptoms",
    basic: "Limited",
    premium: "Unlimited"
  },
  {
    name: "Health Insights",
    description: "Personalized health recommendations",
    basic: false,
    premium: true,
    highlight: true
  },
  {
    name: "Data Export",
    description: "Export your data for doctor visits",
    basic: false,
    premium: true
  },
  {
    name: "Advanced Analytics",
    description: "Detailed cycle analysis and trends",
    basic: false,
    premium: true,
    highlight: true
  },
  {
    name: "Priority Support",
    description: "24/7 priority customer support",
    basic: false,
    premium: true
  },
  {
    name: "Fertility Insights",
    description: "Ovulation tracking and fertility windows",
    basic: "Basic",
    premium: "Advanced",
    highlight: true
  }
];

const FeatureComparison: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'premium'>('premium');

  const renderFeatureValue = (value: boolean | string, isPremium: boolean = false) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className={`w-5 h-5 ${isPremium ? 'text-purple-600' : 'text-green-600'}`} />
      ) : (
        <X className="w-5 h-5 text-gray-400" />
      );
    }
    return (
      <span className={`text-sm font-medium ${isPremium ? 'text-purple-600' : 'text-gray-600'}`}>
        {value}
      </span>
    );
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 rounded-3xl p-8">
      <div className="text-center mb-8">
        <motion.div
          className="inline-flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-full px-4 py-2 mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-gray-700">Choose Your Journey</span>
        </motion.div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Find Your Perfect Plan</h2>
        <p className="text-gray-600">Designed to grow with your wellness needs</p>
      </div>

      {/* Plan Toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-2 border border-white/30">
          <div className="flex space-x-2">
            <motion.button
              onClick={() => setSelectedPlan('basic')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                selectedPlan === 'basic'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-2">
                <Heart className="w-4 h-4" />
                <span>Free Plan</span>
              </div>
            </motion.button>
            <motion.button
              onClick={() => setSelectedPlan('premium')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                selectedPlan === 'premium'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-2">
                <Crown className="w-4 h-4" />
                <span>Premium</span>
              </div>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border border-white/30">
          {/* Header */}
          <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-purple-100 to-pink-100 border-b border-white/30">
            <div>
              <h3 className="font-semibold text-gray-900">Features</h3>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Heart className="w-5 h-5 text-pink-500" />
                <h3 className="font-semibold text-gray-900">Free</h3>
              </div>
              <p className="text-sm text-gray-600">Perfect for getting started</p>
              <div className="mt-3">
                <span className="text-2xl font-bold text-gray-900">$0</span>
                <span className="text-sm text-gray-600">/month</span>
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Crown className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-gray-900">Premium</h3>
                <motion.span
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs px-2 py-1 rounded-full"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Popular
                </motion.span>
              </div>
              <p className="text-sm text-gray-600">Complete wellness journey</p>
              <div className="mt-3">
                <span className="text-2xl font-bold text-purple-600">$9.99</span>
                <span className="text-sm text-gray-600">/month</span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="divide-y divide-gray-100">
            {features.map((feature, index) => (
              <motion.div
                key={feature.name}
                className={`grid grid-cols-3 gap-4 p-4 hover:bg-purple-50/50 transition-all duration-300 ${
                  feature.highlight ? 'bg-purple-50/30 border-l-4 border-l-purple-400' : ''
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex items-center">
                  <div>
                    <h4 className="font-medium text-gray-900 flex items-center">
                      {feature.name}
                      {feature.highlight && (
                        <Star className="w-4 h-4 text-yellow-500 ml-2" />
                      )}
                    </h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  {renderFeatureValue(feature.basic)}
                </div>
                <div className="flex items-center justify-center">
                  {renderFeatureValue(feature.premium, true)}
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-gray-50 to-purple-50">
            <div></div>
            <div className="text-center">
              <motion.button
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started Free
              </motion.button>
            </div>
            <div className="text-center">
              <motion.button
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Premium Trial
              </motion.button>
              <p className="text-xs text-gray-500 mt-2">7-day free trial</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust indicators */}
      <div className="text-center mt-8">
        <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>30-day money back</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="w-4 h-4 text-green-500" />
            <span>Secure payments</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureComparison;